import { useEffect, useMemo } from "react";

import {
  selectedDeploymentHealthLocationVariable,
  useFilterByOffline,
  useSelectedDeploymentHealthLocation,
  useSelectedDeploymentHealthTagLocations,
} from "../variables/deploymentHealth";

import { AutocompleteOptionType } from "../../../common/models/autocomplete";
import { ServiceTypeEnum } from "../../model-manager/variables/modelManager";
import { IParsedDeploymentHealthRow } from "./useDeploymentHealthLeaves";
import { useGetCustomerHealth } from "./useGetCustomerHealth";

export interface IDeploymentHealthTableRow extends IParsedDeploymentHealthRow {
  locationStatus: boolean;
  nodeStatus: boolean;
  deviceStatus: boolean;
  serviceStatus: boolean;
}

interface IUseDeploymentHealthTableRows {
  data?: IDeploymentHealthTableRow[];
  loading: boolean;
  isHealthDataLoaded: boolean;
}

export const useDeploymentHealthTableRows =
  (): IUseDeploymentHealthTableRows => {
    const locationVariable = useSelectedDeploymentHealthLocation();
    const { rows, loading, isHealthDataLoaded } = useGetCustomerHealth();

    // // location value is locationId in the form C#...#L#<locName>
    // const normalizedLocationName = locationVariable?.value
    //   ? locationVariable.value.split("#L#")[1]
    //   : undefined;
    // const { rows, loading, isHealthDataLoaded } = useDeploymentHealthLeaves(
    //   1000,
    //   normalizedLocationName
    // );
    const tagLocationsVariable = useSelectedDeploymentHealthTagLocations();
    const offlineStatus = useFilterByOffline();

    function removeGasLeakType(serviceType: string): string {
      return serviceType.replace(/Gas /g, "");
    }

    function removeSafetyAndSecurityType(serviceType: string): string {
      return serviceType.replace(
        /Safety and Security/g,
        ServiceTypeEnum.PERSONNEL_DETECTION
      );
    }

    useEffect(() => {
      return () => {
        selectedDeploymentHealthLocationVariable(null);
      };
    }, []);

    const data = useMemo(() => {
      if (!rows) return [];

      const mappedData = rows.map((row: IParsedDeploymentHealthRow) => {
        const nodeStatus = row.nodes.every(node => node.status);
        const deviceStatus = row.devices.every(device => device.status);
        const serviceStatus = row.services.every(service => service.status);

        const locationStatus = nodeStatus && deviceStatus && serviceStatus;

        const updatedServices = row.services.map(service => {
          let serviceType = service.serviceType;

          if (service.serviceType.includes("Gas ")) {
            serviceType = removeGasLeakType(service.serviceType);
          }

          if (
            service.serviceType.includes(ServiceTypeEnum.SAFETY_AND_SECURITY)
          ) {
            serviceType = removeSafetyAndSecurityType(service.serviceType);
          }

          return {
            ...service,
            serviceType,
          };
        });

        return {
          ...row,
          services: updatedServices,
          locationStatus,
          nodeStatus,
          deviceStatus,
          serviceStatus,
        };
      });

      let items = [...mappedData];

      if (locationVariable?.value) {
        items = items?.filter(
          (item): boolean => item?.locationId === locationVariable.value
        );
      }

      if (tagLocationsVariable?.length) {
        items = items?.filter((item): boolean => {
          const condition = tagLocationsVariable.some(
            (tag: AutocompleteOptionType) => item?.locationId === tag?.value
          );

          return condition;
        });
      }

      if (
        !locationVariable?.value &&
        tagLocationsVariable &&
        tagLocationsVariable.length === 0
      ) {
        items = [];
      }

      if (!locationVariable?.value && !tagLocationsVariable) {
        items = mappedData;
      }

      if (offlineStatus) {
        items = items?.filter((item): boolean => !item.locationStatus);
      }

      return items.sort((a, b) =>
        a.locationStatus === b.locationStatus ? 0 : a.locationStatus ? 1 : -1
      );
    }, [rows, locationVariable?.value, tagLocationsVariable, offlineStatus]);

    return { data, loading, isHealthDataLoaded };
  };
